import { CellClickedEvent, GridOptions, IRowModel, IRowNode, RowNode } from 'ag-grid-community';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import moment from 'moment';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { UserAdminService } from '../user-admin.service';
import { GridUserFilters, GroupTypeDTO, Language, LDGroupInfo, Permissions, ProfileDTO, SortItemModel, User, UserFilter, UserProfileDTO, UserRowData } from '../user-admin.model';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { ExportItems, TypeFormats } from 'src/shared/types/exportItem';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    encapsulation: ViewEncapsulation.None //allow styling ag-grid
})
export class UserListComponent implements OnInit, OnDestroy {
    @ViewChild('userListGrid') agGrid: AgGridAngular;
    public listUsers: User[] = [];
    @Input() set usersList(userList: User[]) {
        this.listUsers = userList;
        if (this.gridOptions != null && this.agGrid != null) {
            const currentFilters = this.agGrid.api?.getFilterModel();
            this.agGrid.api?.setGridOption('rowData', []); // first clear the table and then add the new data
            this.agGrid.api?.setGridOption('rowData', this.createRowData());
            this.agGrid.api.setFilterModel(currentFilters);
            this.agGrid.api?.setColumnVisible("Impersonate", (this.permissions?.ImpersonateAccess == true) ? true : false);
            this.agGrid.api?.setColumnVisible("SSO", this.permissions.ShouldShowSSO ?? false);
            this.agGrid.api?.sizeColumnsToFit();
        }
    }
    @Input() listProfileDTOs: ProfileDTO[] = [];
    @Input() userProfileDTOs: UserProfileDTO[] = [];
    @Input() baseGroupTypes: string[] = [];
    @Input() groupNames: GroupTypeDTO[] = [];
    @Input() allLDGroups: LDGroupInfo[] = [];
    @Input() langs: Language[];
    @Output() userSelected: EventEmitter<number> = new EventEmitter<number>(); //needs to result in getAllUserData && page index change to 1
    @Output() reloadData: EventEmitter<void> = new EventEmitter<void>();

    public permissions: Permissions;
    public localizationDic: Map<string, string> = new Map();

    //default filter settings
    public activeButtons = {
        active: true,
        inactive: false,
        needsApproval: false,
        lockedOutUser: false,
    };
    public relatedGroupTypeFilter: string = "0";
    public searchFilter: string = "";
    public orderBy: string = 'Username';

    public hasLockedUser: boolean = false;
    public lockedUserCount: number = 0;

    public gridOptions: GridOptions = null;
    public isAddingUser: boolean = false;

    items: ExportItems = {
        excel: {
            getData: (format) => this.exportUserList(format),
        },
        csv: {
            getData: (format) => this.exportUserList(format),
        }
    };

    constructor(
        private uaService: UserAdminService,
        private agGridHelperService: AgGridHelperService,
        private locService: CVLocalizationService,
        configService: CvConfigurationService
    ) {
        if (configService.isCxOne) {
            this.gridOptions.defaultColDef.icons = {
                sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
            };
        }

        this.uaService.userPictureApprovedDenied.subscribe((userProfile: UserProfileDTO) =>{
            const rowNode = this.agGrid.api.getRowNode(userProfile.LoginID.toString());
            rowNode.data.NeedsApproval = !userProfile.isApproved;
            rowNode.data.ImageID = userProfile.ImageID;
            this.agGrid.api.refreshCells({force: true, rowNodes: [rowNode]});
        });
    }

    loadGrid(): void {
        if (this.gridOptions != null && this.agGrid != null) {
            this.agGrid.api?.setGridOption('rowData', []); // first clear the table and then add the new data
            this.agGrid.api?.setGridOption('rowData', this.createRowData());
            this.agGrid.api?.setColumnVisible("Impersonate", (this.permissions?.ImpersonateAccess == true) ? true : false);
            this.agGrid.api?.setColumnVisible("SSO", this.permissions.ShouldShowSSO ?? false);
            this.agGrid.api?.sizeColumnsToFit();
        }
    }
    handleVisibilityChange(): void {
        if (document.visibilityState === 'visible') {
            if (this.gridOptions != null && this.agGrid != null) {
                this.loadGrid();
            } else {
                this.loadLocalizations();
                this.loadGrid();
            }
        }
    }

    ngOnInit(): void {
        this.initAsync();
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }
    ngOnDestroy(): void {
        this.uaService.userPictureApprovedDenied.unsubscribe();
        document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    private filterParams = {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
            var dateAsString = cellValue;
            if (dateAsString == null) return -1;
            var dateParts = dateAsString.split('/');
            var year = dateParts[2].split(' ');
            var cellDate = new Date(
                Number(year[0]),
                Number(dateParts[0]) - 1,
                Number(dateParts[1])
            );

            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                return 0;
            }

            if (cellDate < filterLocalDateAtMidnight) {
                return -1;
            }

            if (cellDate > filterLocalDateAtMidnight) {
                return 1;
            }
            return 0;
        },
        maxValidYear: new Date().getFullYear()
    };

    private dateComparator(date1, date2) {
        var date1Moment = moment(date1);
        var date2Moment = moment(date2);

        if (date1Moment.isValid() == false && date2Moment.isValid() == false) {
            return 0;
        }
        if (date1Moment.isValid() == false) {
            return -1;
        }
        if (date2Moment.isValid() == false) {
            return 1;
        }

        return date1Moment.isBefore(date2Moment) ? -1 : date1Moment.isAfter(date2Moment) ? 1 : 0;
    }

    async initAsync() {
        this.localizationDic = await this.locService.GetSection("Users");
        this.permissions = await this.uaService.getLoginUserPermissions();

        this.loadLocalizations();

        //show impersonate column
        this.agGrid?.api?.setColumnVisible("Impersonate", (this.permissions.ImpersonateAccess == true) ? true : false);
        //show SSO Subject Identifier column
        this.agGrid?.api?.setColumnVisible("SSO", this.permissions.ShouldShowSSO ?? false);
    }

    createOrWriteAccess(): boolean {
        return this.permissions?.CreateAccess || this.permissions?.WriteAccess;
    }

    impersonateUser(loginID?: number) {
        if (this.permissions.ImpersonateAccess) {
            this.uaService.impersonateUser(loginID).then((response) => {
                window.location.replace("../Dashboard/DashboardNew.aspx");
            });
        }
    }

    loadLocalizations() {
        this.locService.GetSection("Users").then((map) => {
            this.localizationDic = map;
        }).finally(() => {
            this.setupGrid();
        });
    }

    getLocalizationString(key, defaultValue) {
        if (this.localizationDic && this.localizationDic.has(key))
            return this.localizationDic.get(key);
        return defaultValue;
    }

    setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: [
                {
                    headerName: this.getLocalizationString("Username", "Username"),
                    colId: "Username",
                    field: "Username",
                    sortable: true,
                    pinned: true,
                    minWidth: 100,
                    cellRenderer: 'usernameCellRenderer'
                },
                {
                    headerName: this.getLocalizationString("Role_Title", "Role"),
                    field: "Role",
                    pinned: false
                },
                {
                    headerName: this.getLocalizationString("SSO_Subject_Identifier", "SSO Subject Identifier"),
                    colId: "SSO",
                    field: "SSO",
                    hide: true,
                    pinned: false,
                    cellStyle: { 'text-overflow': 'ellipsis', 'white-space': 'nowrap', 'overflow': 'hidden', 'display': 'block', 'margin-top': '7px' },
                    tooltipField: "SSO"
                },
                {
                    headerName: this.getLocalizationString("Level_Title", "Level"),
                    field: "Level",
                    pinned: false
                },
                {
                    headerName: this.getLocalizationString("Linked_Data_Group", "Linked Data Group"),
                    field: "LinkedDataGroup",
                    pinned: false
                },
                {
                    headerName: this.getLocalizationString("Parent_Group", "Parent Group"),
                    field: "ParentGroup",
                    pinned: false
                },
                {
                    headerName: this.getLocalizationString("Email_Details", "Email"),
                    field: "Email",
                    pinned: false
                },
                {
                    headerName: this.getLocalizationString("Last_Login", "Last Login"),
                    field: "LastLogin",
                    comparator: this.dateComparator,
                    filter: 'agDateColumnFilter',
                    filterParams: this.filterParams,
                    pinned: false
                },
                {
                    colId: "Impersonate",
                    headerName: "",
                    hide: true,
                    pinned: false,
                    sortable: false,
                    filter: false,
                    menuTabs: [],
                    cellRenderer: 'impersonateButtonRenderer',
                }
            ],
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                suppressMovable: true,
            },
            rowData: this.createRowData(),
            rowClass: "userRow",
            rowSelection: 'single',
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            accentedSort: true,
            animateRows: true,
            alwaysShowVerticalScroll: true,
            rowHeight: 42,
            domLayout: 'autoHeight',
            onModelUpdated: () => this.onModelUpdated(),
            onCellClicked: (event) => this.onCellClicked(event),
            getRowId: ({ data: rowData }) => rowData.LoginID,
            isExternalFilterPresent: () => this.isExternalFilterPresent(),
            doesExternalFilterPass: (node) => this.doesExternalFilterPass(node),
            popupParent: document.querySelector("body"),
            onColumnVisible: (e) => {
                //fixes an issue where the impersonate and sso columns would disappear after clicking Reset Columns 
                this.agGrid?.api?.setColumnVisible("Impersonate", this.permissions?.ImpersonateAccess ?? false);
                this.agGrid?.api?.setColumnVisible("SSO", this.permissions?.ShouldShowSSO ?? false);
                e.api.sizeColumnsToFit();
            },
            onGridSizeChanged: (e) => { e.api.sizeColumnsToFit(); },
            components: {
                'usernameCellRenderer': (param) => {
                    let imgPath: string = param.data.ImageID == undefined || param.data.ImageID == 0 ? '../Admin/ImageResizer.aspx?imgid=&w=32&h=32' :
                        '../Admin/ImageResizer.aspx?imgid=' + param.data.ImageID + '&w=32&h=32';
                    let displayLockedIcon: string = '';
                    let needsApprovalIcon: string = '';

                    if (param.data.NeedsApproval)
                        needsApprovalIcon = '<svg class="icon-12 fill-redRyder" style="position: fixed; left: 36px; top:2px"><use href="../App_Themes/clearview-default/icons/utility/error.svg#error"></use></svg>';

                    if (this.createOrWriteAccess())
                        displayLockedIcon = param.data.AccountLocked ? '<svg class="icon-12 fill-redRyder"><use href="../App_Themes/clearview-default/icons/utility/lock.svg#lock"></use></svg>' : '';

                    return '<span style="display:inline-flex;align-items:center;cursor:pointer;"><img src="' + imgPath + '" style="border-radius: 50%;display:block;width:32px;"/><div style="margin-left:8px;">' + needsApprovalIcon + param.data.Username.replace(/</gi, "&#60;").replace(/=/gi, "&#61;").replace(/>/gi, "&#62;") + '</div></span><div style="float:right;">' + displayLockedIcon + '</div>'
                },
                'impersonateButtonRenderer': (param) => {
                    if (this.permissions['ImpersonateAccess']) {
                        let button = document.createElement("button");
                        $(button).addClass('btn btn-primary btn-sm userImpersonateButton');
                        button.type = "button";
                        button.innerHTML = (!!this.localizationDic) ? this.localizationDic.get("Impersonate") ?? "Impersonate" : "Impersonate";
                        button.addEventListener("click", () => this.impersonateUser(param.data.LoginID));
                        return button;
                    }
                    return;
                }
            },

            onGridReady: (params) => {
                params.columnApi.applyColumnState({
                    state: [
                        { colId: 'LoginName', sort: 'asc' }
                    ]
                }); 
                this.agGrid?.api?.setColumnVisible("Impersonate", this.permissions?.ImpersonateAccess ?? false);
                this.agGrid?.api?.setColumnVisible("SSO", this.permissions?.ShouldShowSSO ?? false);
                params.api.sizeColumnsToFit();
            },
        });
    }

    //#region Implement Export Component
    async exportUserList(format: TypeFormats) {
        const _agFilters = this.agGrid?.api.getFilterModel();
        const agFilters = this.transformFilter(_agFilters);
        const sortModel: SortItemModel[] = this.agGrid?.api.getColumnState().map(c => ({ colId: c.colId, sort: c.sort }));
        const allColumns = this.agGrid?.api.getColumns();
        const displayedColumns = allColumns.filter(column => column.isVisible()).map(column => column.getColId());

        const filters: UserFilter = {
            filters: agFilters,
            sortItem: sortModel[0],
            visibleColumns: displayedColumns,
            search: this.searchFilter,
            relatedGroupType: Number(this.relatedGroupTypeFilter),
            showActives: this.activeButtons.active,
            showInactives: this.activeButtons.inactive,
            showLockedOut: this.activeButtons.lockedOutUser
        };

        return await this.uaService.exportUsers(format, filters);
    };

    transformFilter(filters): GridUserFilters[] {
        const newFilter: GridUserFilters[] = [];

        Object.keys(filters).forEach(f => {
            var filter: GridUserFilters = {
                column: f,
                filterType: filters[f]["filterType"],
                values: filters[f]["values"] == undefined ? [] : filters[f]["values"],
                _operator: filters[f]["operator"] == undefined ? "" : filters[f]["operator"],
                dateConditions: []
            };

            if (filter._operator == "") {
                filter.dateConditions.push({
                    dateFrom: filters[f]["dateFrom"] == undefined ? null : filters[f]["dateFrom"],
                    dateTo: filters[f]["dateTo"] == undefined ? null : filters[f]["dateTo"],
                    dateFilter: filters[f]["type"] == undefined ? "" : filters[f]["type"]
                });
            } else {
                filter.dateConditions.push({
                    dateFrom: filters[f]["condition1"]["dateFrom"] == undefined ? null : filters[f]["condition1"]["dateFrom"],
                    dateTo: filters[f]["condition1"]["dateTo"] == undefined ? null : filters[f]["condition1"]["dateTo"],
                    dateFilter: filters[f]["condition1"]["type"] == undefined ? "" : filters[f]["condition1"]["type"]
                });

                filter.dateConditions.push({
                    dateFrom: filters[f]["condition2"]["dateFrom"] == undefined ? null : filters[f]["condition2"]["dateFrom"],
                    dateTo: filters[f]["condition2"]["dateTo"] == undefined ? null : filters[f]["condition2"]["dateTo"],
                    dateFilter: filters[f]["condition2"]["type"] == undefined ? "" : filters[f]["condition2"]["type"]
                });
            }

            newFilter.push(filter);
        })

        return newFilter;
    }
    //#endregion

    //#region User List AG-Grid

    createRowData(): UserRowData[] {
        var rowData: UserRowData[] = [];

        this.listUsers.forEach((user) => {

            let profile = this.userProfileDTOs.find((p) => p.LoginID == user.LoginID);
            rowData.push({
                LoginID: user.LoginID,
                Username: user.LoginName,
                Role: user.ProfileName,
                SSO: user.ExternalID,
                Level: user.RelatedGroupLevelName,
                LinkedDataGroup: user.RelatedGroupName,
                ParentGroup: user.ParentGroupName,
                Email: user.Email,
                LastLogin: user.LastLogin ? moment(user.LastLogin).format('MM/DD/YYYY h:mm:ss A').toString() : null,
                ImageID: !!profile ? profile.ImageID : 0,
                RelatedGroupType: user.RelatedGroupType,
                Active: user.Active,
                AccountLocked: user.AccountLocked,
                NeedsApproval: !!profile ? !profile.isApproved : false
            });
            if (user.AccountLocked) {
                this.hasLockedUser = true;
                this.lockedUserCount++;
            }
        })

        return rowData;
    }

    doesExternalFilterPass(node: IRowNode): boolean {
        const tmpFilter: string = this.searchFilter.toUpperCase();

        if (!this.activeButtons.active && !this.activeButtons.inactive) return false;
        if (this.activeButtons.active && !this.activeButtons.inactive) {
            if (!node.data.Active) return false;
        }
        if (this.activeButtons.inactive && !this.activeButtons.active) {
            if (node.data.Active) return false;
        }
        if (this.relatedGroupTypeFilter != node.data.RelatedGroupType) return false;
        if (this.activeButtons.needsApproval && !node.data.NeedsApproval) return false;
        if (this.activeButtons.lockedOutUser && !node.data.AccountLocked) return false;

        //-- Empty/Whitespace Input --
        if (tmpFilter.trim() == '')
            return true;

        return (node.data.Username && node.data.Username.toUpperCase().includes(tmpFilter))
            || (node.data.Role && node.data.Role.toUpperCase().includes(tmpFilter))
            || (node.data.SSO && node.data.SSO.toString().toUpperCase().includes(tmpFilter))
            || (node.data.Level && node.data.Level.toString().toUpperCase().includes(tmpFilter))
            || (node.data.LinkedDataGroup && node.data.LinkedDataGroup.toUpperCase().includes(tmpFilter))
            || (node.data.ParentGroup && node.data.ParentGroup.toString().toUpperCase().includes(tmpFilter))
            || (node.data.Email && node.data.Email.toString().toUpperCase().includes(tmpFilter))
            || (node.data.LastLogin && node.data.LastLogin.toString().toUpperCase().includes(tmpFilter));
    }

    isExternalFilterPresent(): boolean { return true; }

    externalFilterChanged() {
        setTimeout(() => {
            if (this.agGrid?.api)
                this.agGrid?.api.onFilterChanged();

            if (this.agGrid?.api?.getDisplayedRowCount() == 0) {
                this.agGrid?.api.showNoRowsOverlay();
            }
            else {
                this.agGrid?.api?.hideOverlay();
            }

        }, 0);
    }

    clearFilter() {
        this.searchFilter = '';
        this.externalFilterChanged();
    }

    onCellClicked(event: CellClickedEvent) {
        if (event.colDef.field != 'Username') return;
        this.userSelected.emit(event.data.LoginID);
        return;
    }

    onModelUpdated() {
        var model: IRowModel = this.agGrid?.api.getModel();
        var totalRows: number = this.gridOptions.rowData.length;
        var processedRows: number = model.getRowCount();
    }

    //#endregion AG-GRID

    //#region add user

    //Open add user modal
    addNewUser() {
        this.isAddingUser = true;
    };

    cancelAddUser() {
        this.isAddingUser = false;
        $("#profilePic_new").css("background-image", "url('../App_Themes/Standard/Images/CoreTheme/Profile/default_profile.svg')")
    };

    addedNewUser() {
        this.isAddingUser = false;
        this.reloadData.emit();
    };

    //#endregion add user
}
